import { Head, router } from '@inertiajs/react'
import { useCallback, useState } from 'react'
import PageHeader from '@/components/common/PageHeader'
import PatientHamburgerMenu from '@/components/common/PatientHamburgerMenu'
import PharmacyMap from '@/components/common/PharmacyMap'
import DocumentUpload from '@/components/reservations/DocumentUpload'
import InterviewForm from '@/components/reservations/InterviewForm'

type FieldOption = {
  value: string
  label: string
}

type InterviewField = {
  key: string
  label: string
  type: 'text' | 'number' | 'textarea' | 'radio' | 'checkbox_group' | 'checkbox' | 'date' | 'file'
  required: boolean
  options?: FieldOption[]
  description?: string
  conditions?: {
    sex?: string
  }
  depends_on?: string
  placeholder?: string
}

type RequiredDocument = {
  key: string
  label: string
  description: string
  required: boolean
}

type Pharmacy = {
  id: number
  name: string
  address: string
  tel: string
  lat: number | null
  lng: number | null
  is_alliance: boolean
}

type Restrictions = {
  restrict_after_examination: boolean
  restrict_after_billing: boolean
  can_edit: boolean
}

type ReservationInfo = {
  clinic_name: string
  medical_department_name: string
  reserved_at: string
  doctor_full_name: string
  doctor_profile_image_url: string
  patient_full_name: string | null
  current_pharmacy_name: string
}

type Paths = {
  show: string
  update: string
  upload_document: string
  selectable_pharmacies: string
  cancel: string
  root: string
}

type Props = {
  examination: { id: number }
  reservation_info: ReservationInfo
  restrictions: Restrictions
  required_documents: RequiredDocument[]
  uploaded_documents: Record<string, string>
  interview_fields: InterviewField[]
  interview_data: Record<string, string | string[]>
  pharmacies: Pharmacy[]
  is_medication_mail: boolean
  selected_pharmacy_id: number | null
  medication_mail_available: boolean
  medication_mail_only: boolean
  errors: Record<string, string[]>
  paths: Paths
  pharmacy_receivable_from: string | null
  medication_mail_delivery_estimate: string
}

const MEDICATION_MAIL_FEE = 900

export default function ExaminationEdit({
  examination,
  reservation_info,
  restrictions,
  required_documents,
  uploaded_documents,
  interview_fields,
  interview_data,
  pharmacies: initialPharmacies,
  is_medication_mail: initialIsMedicationMail,
  selected_pharmacy_id: initialSelectedPharmacyId,
  medication_mail_available,
  medication_mail_only,
  errors,
  paths,
  pharmacy_receivable_from,
  medication_mail_delivery_estimate,
}: Props) {
  const [documents, setDocuments] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const doc of required_documents) {
      const urlKey = `${doc.key}_image_url`
      const value = uploaded_documents[urlKey]
      if (value) initial[doc.key] = value
    }
    return initial
  })

  // フォームの初期値: interview_data はバックエンドで全フィールドキー分埋まっているとは限らない
  const [interviewValues, setInterviewValues] = useState<Record<string, string | string[]>>(() => {
    const initial: Record<string, string | string[]> = {}
    interview_fields.forEach((field) => {
      const existing = interview_data[field.key]
      if (field.type === 'checkbox_group') {
        initial[field.key] = Array.isArray(existing) ? existing : []
      } else if (field.type === 'checkbox') {
        initial[field.key] = (existing as string) || 'false'
      } else {
        initial[field.key] = (existing as string) || ''
      }
    })
    return initial
  })

  const [isMedicationMail, setIsMedicationMail] = useState(initialIsMedicationMail)
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<number | null>(
    initialSelectedPharmacyId,
  )
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>(initialPharmacies)
  const [searchAddress, setSearchAddress] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})
  const [pharmacyError, setPharmacyError] = useState<string | null>(null)

  const examinationId = examination.id

  const handleDocumentUpload = useCallback((key: string, result: { url: string }) => {
    setDocuments((prev) => {
      if (result.url) return { ...prev, [key]: result.url }
      const next = { ...prev }
      delete next[key]
      return next
    })
    setLocalErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleDocumentError = useCallback((key: string, error: string) => {
    setLocalErrors((prev) => ({ ...prev, [key]: error }))
  }, [])

  const shouldShowField = useCallback(
    (field: InterviewField): boolean => {
      // PMS等のsex条件は examination が既に作成済みなので
      // patient_family_profile.sex で判断するのが本来だが、edit時点では既に正しく作成されている前提で
      // 表示時はサーバから渡された interview_data のキーが入っているかで切り替える簡易判定とする。
      // ただし条件の指定が無いフィールドは常に表示する。
      if (field.depends_on) {
        const [dependsField, dependsValue] = field.depends_on.split('.')
        const currentValue = interviewValues[dependsField]
        if (Array.isArray(currentValue)) return currentValue.includes(dependsValue)
        return currentValue === dependsValue
      }
      return true
    },
    [interviewValues],
  )

  const handleFieldChange = useCallback((key: string, value: string | string[]) => {
    setInterviewValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleSearchPharmacies = useCallback(async () => {
    if (!searchAddress.trim()) return

    setIsSearching(true)
    setSearchError('')

    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

    try {
      const response = await fetch(paths.selectable_pharmacies, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ examination_id: examinationId, address: searchAddress.trim() }),
      })

      if (!response.ok) {
        setSearchError('薬局の検索に失敗しました。しばらく経ってからお試しください。')
        return
      }

      const data = await response.json()
      if (data.success) {
        setPharmacies(data.pharmacies)
        setSelectedPharmacyId(null)
        if (data.center_position) setMapCenter(data.center_position)
      } else {
        setSearchError(data.error || '薬局の検索に失敗しました。')
      }
    } catch {
      setSearchError('薬局の検索に失敗しました。しばらく経ってからお試しください。')
    } finally {
      setIsSearching(false)
    }
  }, [examinationId, paths.selectable_pharmacies, searchAddress])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSearchPharmacies()
      }
    },
    [handleSearchPharmacies],
  )

  const handleSubmit = useCallback(() => {
    if (!restrictions.can_edit) return
    if (!isMedicationMail && !selectedPharmacyId) {
      setPharmacyError('薬局を選択してください')
      return
    }
    setPharmacyError(null)
    setSubmitError(null)
    setIsSubmitting(true)

    // 診察終了後は問診/書類の改変が禁止のため、薬局・おうち便のみ送信する。
    const documentUrls: Record<string, string> = {}
    for (const doc of required_documents) {
      documentUrls[`${doc.key}_image_url`] = documents[doc.key] || ''
    }
    const payload = restrictions.restrict_after_examination
      ? {
          is_medication_mail: isMedicationMail,
          pharmacy_id: isMedicationMail ? null : selectedPharmacyId,
        }
      : {
          interview_step2: interviewValues,
          document_urls: documentUrls,
          is_medication_mail: isMedicationMail,
          pharmacy_id: isMedicationMail ? null : selectedPharmacyId,
        }

    router.patch(paths.update, payload, {
      onError: () => {
        setSubmitError('入力内容に誤りがあります。エラーメッセージをご確認ください。')
      },
      onFinish: () => {
        setIsSubmitting(false)
      },
    })
  }, [
    documents,
    interviewValues,
    isMedicationMail,
    paths.update,
    required_documents,
    restrictions.can_edit,
    restrictions.restrict_after_examination,
    selectedPharmacyId,
  ])

  const selectedPharmacy = pharmacies.find((p) => p.id === selectedPharmacyId)
  const editingDisabled = !restrictions.can_edit

  return (
    <>
      <Head title="問診内容の修正 - オンライン診療" />
      <div className="min-h-screen bg-[#f4f6f8] pb-32">
        <PageHeader trailing={<PatientHamburgerMenu />} subtitle="問診内容の修正" />

        <main className="mx-auto max-w-3xl px-4 py-6 space-y-4">
          <div className="oc-card">
            <h2 className="oc-section-title">予約情報</h2>
            <dl className="space-y-2 text-[14px] text-[#2a3039]">
              <div className="flex">
                <dt className="w-[7rem] text-[#6f7680]">診療機関</dt>
                <dd className="flex-1">{reservation_info.clinic_name}</dd>
              </div>
              <div className="flex">
                <dt className="w-[7rem] text-[#6f7680]">診療科目</dt>
                <dd className="flex-1">{reservation_info.medical_department_name}</dd>
              </div>
              <div className="flex">
                <dt className="w-[7rem] text-[#6f7680]">予約日時</dt>
                <dd className="flex-1">{reservation_info.reserved_at}</dd>
              </div>
              <div className="flex">
                <dt className="w-[7rem] text-[#6f7680]">担当医師</dt>
                <dd className="flex-1">{reservation_info.doctor_full_name}先生</dd>
              </div>
              {reservation_info.patient_full_name && (
                <div className="flex">
                  <dt className="w-[7rem] text-[#6f7680]">来院される方</dt>
                  <dd className="flex-1">{reservation_info.patient_full_name}さま</dd>
                </div>
              )}
            </dl>
          </div>

          {Object.keys(errors).length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <ul className="text-sm text-red-600 list-disc list-inside">
                {Object.entries(errors).map(([key, messages]) =>
                  messages.map((msg, i) => <li key={`${key}-${i}`}>{msg}</li>),
                )}
              </ul>
            </div>
          )}

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          {restrictions.restrict_after_examination && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                診察が終了しているため、問診内容と添付書類は変更できません。受け取り薬局・おくすりおうち便のみ変更可能です。
              </p>
            </div>
          )}

          {!restrictions.restrict_after_examination && (
          <>
          <div className="oc-card">
            <h2 className="oc-section-title">添付書類</h2>
            <p className="mb-5 text-sm text-[#6f7680]">必要書類を更新する場合はアップロードしてください。</p>
            {Object.keys(localErrors).length > 0 && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {Object.entries(localErrors).map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              {required_documents.map((doc) => (
                <DocumentUpload
                  key={doc.key}
                  documentType={doc.key}
                  label={doc.label}
                  description={doc.description}
                  required={doc.required}
                  initialUrl={documents[doc.key]}
                  uploadEndpoint={paths.upload_document}
                  extraFormData={{ examination_id: String(examinationId) }}
                  showRemoveButton={!doc.required}
                  onUploadComplete={(result) => handleDocumentUpload(doc.key, result)}
                  onError={(error) => handleDocumentError(doc.key, error)}
                />
              ))}
            </div>
          </div>

          <div className="oc-card">
            <h2 className="oc-section-title">問診内容</h2>
            <p className="mb-5 text-sm text-[#6f7680]">
              診察に必要な情報を入力してください。各項目の「必須」バッジをご確認ください。
            </p>
            <InterviewForm
              fields={interview_fields}
              values={interviewValues}
              errors={errors}
              onChange={handleFieldChange}
              shouldShowField={shouldShowField}
            />
          </div>
          </>
          )}

          <div className="oc-card">
            <h2 className="oc-section-title">処方せんの受け取り方法</h2>

            {medication_mail_only ? (
              // 肥満症外来はおうち便で固定
              <div className="p-4 border border-cyan-500 bg-cyan-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">おくすりおうち便</span>
                  <span className="text-sm text-cyan-600 font-medium">{MEDICATION_MAIL_FEE}円</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  ※肥満症外来はおくすりおうち便のみご利用いただけます
                </p>
              </div>
            ) : (
              <>
                <p className="mb-6 text-sm text-[#6f7680]">処方せんの受け取り方法を選択してください。</p>
                <div className="space-y-4">
                  <label
                    className={`block p-4 border rounded-lg cursor-pointer transition ${
                      !isMedicationMail
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="delivery_method"
                        checked={!isMedicationMail}
                        onChange={() => setIsMedicationMail(false)}
                        className="oc-radio mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">薬局で受け取り</span>
                          <span className="text-sm text-gray-500">無料</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          お近くの薬局で処方せんを受け取り、お薬を受け取ります。
                          {!pharmacy_receivable_from && 'お急ぎの場合におすすめです。'}
                        </p>
                        {pharmacy_receivable_from && (
                          <p className="text-sm font-bold text-orange-600 mt-1">
                            ※土曜午後・日曜・祝日は薬局への処方せんの送付をおこなっていないため、お薬のお受け取りは{pharmacy_receivable_from}以降になります。
                            {medication_mail_available && 'ご自宅にお薬をお届けする「おくすりおうち便」の利用もご検討ください。'}
                          </p>
                        )}
                      </div>
                    </div>
                  </label>
                  <label
                    className={`block p-4 border rounded-lg transition ${
                      medication_mail_available ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                    } ${
                      isMedicationMail
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="delivery_method"
                        checked={isMedicationMail}
                        onChange={() => medication_mail_available && setIsMedicationMail(true)}
                        disabled={!medication_mail_available}
                        className="oc-radio mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">おくすりおうち便</span>
                          <span className="text-sm text-cyan-600 font-medium">
                            {MEDICATION_MAIL_FEE}円
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。
                        </p>
                        {!medication_mail_available && (
                          <p className="text-sm text-red-500 mt-1">
                            ※コロナ外来で東京都以外にお住まいの方はご利用いただけません
                          </p>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          {!isMedicationMail && !medication_mail_only && (
            <div className="oc-card">
              <h2 className="oc-section-title">薬局を選択</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  住所で薬局を検索
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="例: 東京都渋谷区"
                    className="oc-input min-w-0"
                  />
                  <button
                    type="button"
                    onClick={handleSearchPharmacies}
                    disabled={isSearching}
                    className="button-primary !w-auto !h-auto shrink-0 whitespace-nowrap px-5"
                  >
                    {isSearching ? '検索中...' : '検索'}
                  </button>
                </div>
              </div>
              {searchError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-600">{searchError}</p>
                </div>
              )}
              {(pharmacies.length > 0 || mapCenter) && (
                <div className="mb-4">
                  <PharmacyMap
                    pharmacies={pharmacies}
                    centerLat={mapCenter?.lat}
                    centerLng={mapCenter?.lng}
                    selectedPharmacyId={selectedPharmacyId}
                    onSelectPharmacy={(id) => {
                      setSelectedPharmacyId(id)
                      setPharmacyError(null)
                      setTimeout(() => {
                        document
                          .getElementById(`pharmacy-${id}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                      }, 0)
                    }}
                  />
                </div>
              )}
              <div className="space-y-2">
                {pharmacies.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    お近くの薬局が見つかりません。住所を入力して検索してください。
                  </p>
                ) : (
                  pharmacies.map((pharmacy) => (
                    <label
                      key={pharmacy.id}
                      id={`pharmacy-${pharmacy.id}`}
                      className={`block p-4 border rounded-lg cursor-pointer transition ${
                        selectedPharmacyId === pharmacy.id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="pharmacy"
                          checked={selectedPharmacyId === pharmacy.id}
                          onChange={() => {
                            setSelectedPharmacyId(pharmacy.id)
                            setPharmacyError(null)
                          }}
                          className="oc-radio mt-1"
                        />
                        <div className="ml-3 flex-1">
                          <span className="font-medium text-gray-800">{pharmacy.name}</span>
                          <p className="text-sm text-gray-500 mt-1">{pharmacy.address}</p>
                          <p className="text-sm text-gray-500">TEL: {pharmacy.tel}</p>
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {pharmacyError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{pharmacyError}</p>
            </div>
          )}

          <div className="oc-card">
            <h2 className="oc-section-title">お受け取り方法</h2>
            {isMedicationMail ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">おくすりおうち便</p>
                  <p className="text-sm text-gray-500">お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。</p>
                </div>
                <span className="text-cyan-600 font-medium">{MEDICATION_MAIL_FEE}円</span>
              </div>
            ) : selectedPharmacy ? (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">{selectedPharmacy.name}</p>
                <p className="text-sm text-gray-500">{selectedPharmacy.address}</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">薬局を選択してください</p>
            )}
          </div>
        </main>

        <div className="oc-action-bar">
          <div className="oc-action-container">
            <button
              type="button"
              onClick={() => router.visit(paths.show)}
              className="button-secondary"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={editingDisabled || isSubmitting}
              className="button-primary"
            >
              {isSubmitting ? '更新中...' : '更新する'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
